import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Badge,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@asm/ui";
import {
  Settings as SettingsIcon,
  User,
  Building2,
  Key,
  Languages,
  CheckCircle2,
  AlertTriangle,
  Users,
  Mail,
  Trash2,
  Loader2,
  MailPlus,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { supportedLanguages } from "@/i18n";
import { userApi } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";

export default function Settings() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation("settings");

  const { data: profile, isLoading } = useQuery({
    queryKey: ["userProfile"],
    queryFn: userApi.getProfile,
  });

  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [orgNameSuccess, setOrgNameSuccess] = useState(false);

  useEffect(() => {
    if (profile?.name) {
      setName(profile.name);
    }
    if (profile?.org_name) {
      setOrgName(profile.org_name);
    }
  }, [profile]);

  const updateProfile = useMutation({
    mutationFn: userApi.updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userProfile"] });
    },
  });

  const handleUpdateProfile = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile.mutate({ name });
  };

  const renameOrg = useMutation({
    mutationFn: (newName: string) => userApi.renameOrganization(newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userProfile"] });
      setOrgNameSuccess(true);
      setTimeout(() => setOrgNameSuccess(false), 3000);
    },
  });

  const handleRenameOrg = () => {
    const trimmed = orgName.trim();
    if (!trimmed || trimmed === profile?.org_name) return;
    renameOrg.mutate(trimmed);
  };

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
  };

  // Team management (admin only)
  const isAdmin = profile?.role === "admin";

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ["teamMembers"],
    queryFn: userApi.listMembers,
    enabled: isAdmin,
  });

  const { data: invitationsData, isLoading: invitationsLoading, refetch: refetchInvitations } = useQuery({
    queryKey: ["teamInvitations"],
    queryFn: userApi.listInvitations,
    enabled: isAdmin,
  });

  const members = membersData ?? [];
  const invitations = invitationsData ?? [];

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [teamError, setTeamError] = useState<string | null>(null);
  const [teamSuccess, setTeamSuccess] = useState<string | null>(null);

  const inviteMutation = useMutation({
    mutationFn: userApi.inviteUser,
    onSuccess: () => {
      setInviteEmail("");
      setInviteRole("member");
      setTeamSuccess(t("organization.inviteSuccess"));
      setTeamError(null);
      refetchInvitations();
      setTimeout(() => setTeamSuccess(null), 3000);
    },
    onError: (err) => {
      setTeamError(err instanceof Error ? err.message : t("organization.inviteFailed"));
    },
  });

  const cancelInvitationMutation = useMutation({
    mutationFn: userApi.cancelInvitation,
    onSuccess: () => {
      refetchInvitations();
    },
    onError: (err) => {
      setTeamError(err instanceof Error ? err.message : "Failed to cancel invitation");
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: userApi.removeUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teamMembers"] });
    },
    onError: (err) => {
      setTeamError(err instanceof Error ? err.message : "Failed to remove user");
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) => userApi.updateUserRole(id, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teamMembers"] });
    },
    onError: (err) => {
      setTeamError(err instanceof Error ? err.message : "Failed to update role");
    },
  });

  const handleInvite = () => {
    if (!inviteEmail.trim()) return;
    setTeamError(null);
    setTeamSuccess(null);
    inviteMutation.mutate({ email: inviteEmail.trim(), role: inviteRole });
  };

  const handleRemoveUser = (userId: string) => {
    if (!confirm(t("organization.removeConfirm"))) return;
    removeUserMutation.mutate(userId);
  };

  // Pending invitation for current user (from another org)
  const { data: pendingInvitation } = useQuery({
    queryKey: ["pendingInvitation"],
    queryFn: userApi.getPendingInvitation,
    refetchInterval: 60_000, // Check every minute
  });

  const acceptInvitationMutation = useMutation({
    mutationFn: userApi.acceptInvitation,
    onSuccess: () => {
      // User's org changed — invalidate everything and reload
      queryClient.invalidateQueries();
      window.location.reload();
    },
  });

  const declineInvitationMutation = useMutation({
    mutationFn: userApi.declineInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pendingInvitation"] });
    },
  });

  return (
    <div className="tactical-grid space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
            <SettingsIcon className="h-8 w-8 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t("title")}</h1>
            <p className="text-muted-foreground">{t("description")}</p>
          </div>
        </div>
      </div>

      {/* Pending Invitation Banner */}
      {pendingInvitation && (
        <div className="animate-fade-in-up rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <MailPlus className="h-5 w-5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-amber-200">
                {t("invitation.title")}
              </h3>
              <p className="text-sm text-amber-200/80 mt-1">
                {t("invitation.description", {
                  org: pendingInvitation.org_name,
                  role: t(`organization.${pendingInvitation.role}`),
                })}
              </p>
              {pendingInvitation.invited_by_email && (
                <p className="text-xs text-amber-200/60 mt-1">
                  {t("invitation.invitedBy", { email: pendingInvitation.invited_by_email })}
                </p>
              )}
              <div className="flex items-center gap-2 mt-3">
                <Button
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                  onClick={() => acceptInvitationMutation.mutate()}
                  disabled={acceptInvitationMutation.isPending || declineInvitationMutation.isPending}
                >
                  {acceptInvitationMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : null}
                  {t("invitation.accept")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-amber-200/80 hover:text-amber-200 hover:bg-amber-500/20"
                  onClick={() => declineInvitationMutation.mutate()}
                  disabled={acceptInvitationMutation.isPending || declineInvitationMutation.isPending}
                >
                  {t("invitation.decline")}
                </Button>
              </div>
              {acceptInvitationMutation.isError && (
                <p className="text-xs text-red-400 mt-2">
                  {acceptInvitationMutation.error instanceof Error
                    ? acceptInvitationMutation.error.message
                    : t("invitation.error")}
                </p>
              )}
            </div>
            <button
              className="text-amber-200/60 hover:text-amber-200"
              onClick={() => declineInvitationMutation.mutate()}
              disabled={declineInvitationMutation.isPending}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="bg-muted/50 p-1">
          <TabsTrigger value="profile" className="data-[state=active]:bg-background gap-2">
            <User className="h-4 w-4" />
            {t("tabs.profile")}
          </TabsTrigger>
          <TabsTrigger value="organization" className="data-[state=active]:bg-background gap-2">
            <Building2 className="h-4 w-4" />
            {t("tabs.organization")}
          </TabsTrigger>
          <TabsTrigger value="api-keys" className="data-[state=active]:bg-background gap-2">
            <Key className="h-4 w-4" />
            {t("tabs.apiKeys")}
          </TabsTrigger>
          <TabsTrigger value="language" className="data-[state=active]:bg-background gap-2">
            <Languages className="h-4 w-4" />
            {t("tabs.language")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="animate-fade-in-up rounded-lg border border-border bg-card/50 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-6 w-1 rounded-full bg-cyan-500" />
              <div>
                <h2 className="text-lg font-semibold">{t("profile.title")}</h2>
                <p className="text-xs text-muted-foreground">{t("profile.description")}</p>
              </div>
            </div>

            <form onSubmit={handleUpdateProfile} className="space-y-6">
              {updateProfile.error && (
                <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {updateProfile.error instanceof Error
                    ? updateProfile.error.message
                    : t("profile.failed")}
                </div>
              )}
              {updateProfile.isSuccess && (
                <div className="p-3 text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  {t("profile.success")}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-muted-foreground">{t("profile.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile?.email || user?.email || ""}
                  disabled
                  className="bg-muted/50 border-border font-mono"
                />
                <p className="text-xs text-muted-foreground">{t("profile.emailHint")}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name" className="text-muted-foreground">{t("profile.name")}</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder={t("profile.namePlaceholder")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isLoading}
                  className="bg-muted/50 border-border"
                />
              </div>

              <Button
                type="submit"
                disabled={updateProfile.isPending}
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
              >
                {updateProfile.isPending ? t("profile.saving") : t("profile.save")}
              </Button>
            </form>
          </div>
        </TabsContent>

        <TabsContent value="organization">
          <div className="space-y-6">
            {/* Org Info Card */}
            <div className="animate-fade-in-up rounded-lg border border-border bg-card/50 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-6 w-1 rounded-full bg-cyan-500" />
                <div>
                  <h2 className="text-lg font-semibold">{t("organization.title")}</h2>
                  <p className="text-xs text-muted-foreground">{t("organization.description")}</p>
                </div>
              </div>

              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-16 w-full rounded-lg" />
                  <Skeleton className="h-16 w-full rounded-lg" />
                  <Skeleton className="h-16 w-full rounded-lg" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-lg border border-border bg-muted/30 p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                      {t("organization.name")}
                    </p>
                    {isAdmin ? (
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          value={orgName}
                          onChange={(e) => setOrgName(e.target.value)}
                          className="bg-muted/50 border-border h-9 flex-1"
                          placeholder={t("organization.namePlaceholder")}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); handleRenameOrg(); }
                          }}
                        />
                        <Button
                          size="sm"
                          className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold h-9"
                          onClick={handleRenameOrg}
                          disabled={renameOrg.isPending || !orgName.trim() || orgName.trim() === profile?.org_name}
                        >
                          {renameOrg.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            t("organization.rename")
                          )}
                        </Button>
                      </div>
                    ) : (
                      <p className="font-medium">{profile?.org_name || "—"}</p>
                    )}
                    {orgNameSuccess && (
                      <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        {t("organization.renameSuccess")}
                      </p>
                    )}
                    {renameOrg.isError && (
                      <p className="text-xs text-red-400 mt-1">
                        {renameOrg.error instanceof Error ? renameOrg.error.message : t("organization.renameFailed")}
                      </p>
                    )}
                  </div>
                  <div className="rounded-lg border border-border bg-muted/30 p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                      {t("organization.role")}
                    </p>
                    <Badge
                      className={
                        profile?.role === "admin"
                          ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
                          : "bg-muted text-muted-foreground"
                      }
                    >
                      {profile?.role || "member"}
                    </Badge>
                  </div>
                  <div className="rounded-lg border border-border bg-muted/30 p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                      {t("organization.memberSince")}
                    </p>
                    <p className="font-medium">
                      {profile?.created_at
                        ? new Date(profile.created_at).toLocaleDateString()
                        : "—"}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Team Management (admin only) */}
            {isAdmin && (
              <>
                {/* Status Messages */}
                {teamError && (
                  <div className="animate-fade-in-up p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    {teamError}
                  </div>
                )}
                {teamSuccess && (
                  <div className="animate-fade-in-up p-3 text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    {teamSuccess}
                  </div>
                )}

                {/* Invite Form */}
                <div className="animate-fade-in-up rounded-lg border border-border bg-card/50 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-6 w-1 rounded-full bg-cyan-500" />
                    <div>
                      <h2 className="text-lg font-semibold">{t("organization.invite")}</h2>
                    </div>
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs text-muted-foreground">{t("organization.inviteEmailLabel")}</Label>
                      <Input
                        type="email"
                        placeholder={t("organization.invitePlaceholder")}
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        className="bg-muted/50 border-border"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); handleInvite(); }
                        }}
                      />
                    </div>
                    <div className="w-32 space-y-1">
                      <Label className="text-xs text-muted-foreground">{t("organization.roleLabel")}</Label>
                      <Select value={inviteRole} onValueChange={setInviteRole}>
                        <SelectTrigger className="bg-muted/50 border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">{t("organization.admin")}</SelectItem>
                          <SelectItem value="member">{t("organization.member")}</SelectItem>
                          <SelectItem value="viewer">{t("organization.viewer")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
                      onClick={handleInvite}
                      disabled={inviteMutation.isPending || !inviteEmail.trim()}
                    >
                      {inviteMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        t("organization.invite")
                      )}
                    </Button>
                  </div>
                </div>

                {/* Team Members */}
                <div className="animate-fade-in-up rounded-lg border border-border bg-card/50 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-6 w-1 rounded-full bg-cyan-500" />
                    <div>
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        {t("organization.team")} ({members.length})
                      </h2>
                      <p className="text-xs text-muted-foreground">{t("organization.teamDescription")}</p>
                    </div>
                  </div>

                  {membersLoading ? (
                    <div className="space-y-2">
                      {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full rounded" />)}
                    </div>
                  ) : members.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">{t("organization.noMembers")}</p>
                  ) : (
                    <div className="rounded-lg border border-border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30 hover:bg-muted/30">
                            <TableHead className="font-semibold text-xs">{t("profile.email")}</TableHead>
                            <TableHead className="font-semibold text-xs">{t("profile.name")}</TableHead>
                            <TableHead className="font-semibold text-xs">{t("organization.roleLabel")}</TableHead>
                            <TableHead className="w-[60px]" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {members.map((m) => (
                            <TableRow key={m.id} className="hover:bg-muted/50">
                              <TableCell className="text-sm font-mono">{m.email}</TableCell>
                              <TableCell className="text-sm">{m.name || "—"}</TableCell>
                              <TableCell>
                                {m.id === profile?.id ? (
                                  <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 capitalize">
                                    {m.role}
                                  </Badge>
                                ) : (
                                  <Select
                                    value={m.role}
                                    onValueChange={(role) => updateRoleMutation.mutate({ id: m.id, role })}
                                    disabled={updateRoleMutation.isPending}
                                  >
                                    <SelectTrigger className="h-7 w-24 text-xs bg-transparent border-border">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="admin">{t("organization.admin")}</SelectItem>
                                      <SelectItem value="member">{t("organization.member")}</SelectItem>
                                      <SelectItem value="viewer">{t("organization.viewer")}</SelectItem>
                                    </SelectContent>
                                  </Select>
                                )}
                              </TableCell>
                              <TableCell>
                                {m.id !== profile?.id && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 hover:bg-red-500/10 hover:text-red-400"
                                    onClick={() => handleRemoveUser(m.id)}
                                    title={t("organization.remove")}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>

                {/* Pending Invitations */}
                <div className="animate-fade-in-up rounded-lg border border-border bg-card/50 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-6 w-1 rounded-full bg-cyan-500" />
                    <div>
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        {t("organization.pendingInvitations")} ({invitations.length})
                      </h2>
                    </div>
                  </div>

                  {invitationsLoading ? (
                    <div className="space-y-2">
                      {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-12 w-full rounded" />)}
                    </div>
                  ) : invitations.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">{t("organization.noInvitations")}</p>
                  ) : (
                    <div className="rounded-lg border border-border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30 hover:bg-muted/30">
                            <TableHead className="font-semibold text-xs">{t("profile.email")}</TableHead>
                            <TableHead className="font-semibold text-xs">{t("organization.roleLabel")}</TableHead>
                            <TableHead className="font-semibold text-xs">{t("organization.expires")}</TableHead>
                            <TableHead className="w-[60px]" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invitations.map((inv) => (
                            <TableRow key={inv.id} className="hover:bg-muted/50">
                              <TableCell className="text-sm font-mono">{inv.email}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="capitalize">{inv.role}</Badge>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {new Date(inv.expires_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 hover:bg-red-500/10 hover:text-red-400"
                                  onClick={() => cancelInvitationMutation.mutate(inv.id)}
                                  title={t("organization.cancel")}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="api-keys">
          <div className="animate-fade-in-up rounded-lg border border-border bg-card/50 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-6 w-1 rounded-full bg-cyan-500" />
              <div>
                <h2 className="text-lg font-semibold">{t("apiKeys.title")}</h2>
                <p className="text-xs text-muted-foreground">{t("apiKeys.description")}</p>
              </div>
            </div>

            <div className="text-center py-12">
              <div className="p-4 rounded-xl bg-muted/30 border border-border mb-6 inline-block">
                <Key className="h-12 w-12 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground text-sm mb-4">
                {t("apiKeys.adminOnly")}
              </p>
              {profile?.role === "admin" && (
                <Button variant="outline">{t("apiKeys.manage")}</Button>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="language">
          <div className="animate-fade-in-up rounded-lg border border-border bg-card/50 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-6 w-1 rounded-full bg-cyan-500" />
              <div>
                <h2 className="text-lg font-semibold">{t("language.title")}</h2>
                <p className="text-xs text-muted-foreground">{t("language.description")}</p>
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-muted-foreground">{t("language.select")}</Label>
              <div className="flex flex-wrap gap-3">
                {supportedLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`
                      module-card px-4 py-3 rounded-lg flex items-center gap-2 min-w-[140px]
                      ${i18n.language === lang.code ? "selected glow-cyan" : ""}
                    `}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span className="font-medium">{lang.name}</span>
                    {i18n.language === lang.code && (
                      <CheckCircle2 className="h-4 w-4 text-cyan-400 ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
